/* eslint-disable indent */
import '../css/typography.css';

import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import CarvanaFooter from '@carvana/footer';
import HeaderSkeleton, { getHeaderManifest } from '@carvana/header';
import ThemeProvider, { CarvanaThemeDeprecated, CarvanaTheme } from '@carvana/showroom-theme';
import { MfeConfig } from '@carvana/microfrontend';
import { MfeReact, MfeReactComponent } from '@carvana/microfrontend-react';
import { AnalyticsPlugin } from '@carvana/microfrontend-plugins';
import { createClient, Provider as UrqlProvider, dedupExchange, fetchExchange } from 'urql';
import { AnalyticsProvider } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { BreakpointProvider } from '../helpers/breakpoint-provider';
import { SupportCenterProvider } from '../helpers/context-provider';
import { getAuthHeaders } from '../utils/get-auth-headers';
import { useAnalytics } from '../utils/analytics-provider/analytics-provider';
import { OnSiteUnload } from '../helpers/send-in-view-event';
import { ErrorBoundary } from '../helpers/error-boundary';
import { RegisterExperiment } from '@carvana/core-experiment';

import '@carvana/showroom-css-theme/Reset.css';
import '@carvana/showroom-css-theme/Font.css';
import '@carvana/header/styles.css';

const Wrapper = styled.main`
  margin: 0 auto;
  box-shadow: rgba(24, 53, 88, 0.1) 1px 2px 3px 1px;
  min-width: 375px;
  max-width: 1800px;
`;

const env = process.env.GATSBY_ACTIVE_ENV === 'prod' ? 'production' : 'test';

const HeaderPlaceholder = () => <div style={{ height: '79px' }} />;

const Layout = ({ children }) => {
  const { trackEvent } = useAnalytics();

  OnSiteUnload({ trackEvent });
  const client = useMemo(() => createClient({
    url: process.env.GATSBY_NODE_API_URL,
    fetchOptions: () => ({
      headers: getAuthHeaders(),
      credentials: 'same-origin'
    }),
    exchanges: [
      dedupExchange, // dedupe first so we don't do unnecessary work
      fetchExchange // async, so it needs to go last
    ]
  }), []);

  useEffect(() => {
    RegisterExperiment({
      environment: env,
      projectName: 'support-hub'
    });

    MfeConfig(
      {
        name: 'support-hub',
        ENV: env,
        Render: MfeReact(),
        debug: false,
        globals: {
          React: () => import('react'),
          ReactDOM: () => import('react-dom'),
          'react-dom': () => import('react-dom'),
          styled: () => import('styled-components'),
          'styled-components': () => import('styled-components'),
          'prop-types': () => import('prop-types'),
          PropTypes: () => import('prop-types')
        },
        files: [
          [
            'HeaderModule',
            [
              {
                component: 'default',
                browser: true,
                default: {
                  file: 'index.js',
                  manifest: getHeaderManifest(process.env.GATSBY_TARGET_ENV)
                }
              }
            ]
          ],
          [
            'FooterModule',
            [
              {
                component: 'default',
                browser: true,
                default: {
                  file: 'index.js',
                  manifest: `${
                    process.env.GATSBY_CDN_BASE_URL
                  }/footer/module/assets-manifest.json?v=${Date.now()}`
                }
              }
            ]
          ]
        ]
      },
      {
        plugins: [
          AnalyticsPlugin({
            repository: 'Carvana.SupportHub',
            ddr: datadogRum
          })
        ]
      }
    );
  }, []);

  return (
    <UrqlProvider value={client}>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no, user-scalable=no"
        />
        <script src="https://www.youtube.com/iframe_api"></script>
      </Helmet>
      <ThemeProvider theme={{ ...CarvanaTheme, ...CarvanaThemeDeprecated }}>
        <BreakpointProvider>
          <MfeReactComponent
            component="default"
            module="HeaderModule"
            environment={env}
            chatContext="carvana_supporthub"
            loading={<HeaderSkeleton environment={env} shouldLoadPlugin={false} />}
          />
          <AnalyticsProvider
            teamName="core"
            track="core"
            projectName="support_center"
            environment={env}
          >
            <SupportCenterProvider>
              <ErrorBoundary>
                <Wrapper>{children}</Wrapper>
              </ErrorBoundary>
            </SupportCenterProvider>
          </AnalyticsProvider>

          <MfeReactComponent
            component="default"
            module="FooterModule"
            environment={env}
            withoutProvider
            loading={() => <CarvanaFooter environment={env} withoutProvider />}
          />
        </BreakpointProvider>
      </ThemeProvider>
    </UrqlProvider>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired
};

export default Layout;
